import {
  Box,
  Button,
  Divider,
  Tab,
  Tabs,
  styled,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { Gear, Plus } from '@phosphor-icons/react';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import theme from '../../theme';
import AgentInfo from './AgentTabSection/AgentInfo';
import FunctionTabs from './FunctionTabSection/FunctionTabs';
import ExtractionDataTable from './ExtractionDataTabSection/ExtractionDataTable';
import DeployUrlsModal from '../modals/DeployUrlsModal';
import { useAgents } from '../../hooks/useAgents';
import { useDashboard } from '../../hooks/useDashboard';
import OutboundCalls from './Outbound-Calls/OutboundCalls';
import VersionHistoryCards from './VersionHistory/VersionHistoryCards';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';
import ConfigurationDrawer from './AgentTabSection/AdvancAgentConfiguration/ConfigurationDrawer';
// import ChatSimulation from './ChatSimulation';
import WebHookFunctionDrawer from './FunctionTooling/WebHookFunctionDrawer';
import WebFormFunctionDrawer from './FunctionTooling/WebFormFunctionDrawer';
import { useDispatch } from 'react-redux';
import { resetFunctionBuilder } from '../../utils/redux/slices/webhookFunctionSlice';
import { resetWebForm } from '../../utils/redux/slices/webformSlice';
import AgentMemory from './AgentMemory/AgentMemory';
import VersionHistorySidebar from './AgentTabSection/VersionHistorySidebar';
import FlowHistorySidebar from './AgentTabSection/FlowHistorySidebar'; // import the new sidebar
import ToolCreator from './FunctionTabSection/ToolCreationTabSection/ToolCreator';
import AudioUpload from './AudioUpload/AudioUpload';

const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '16px',
    color: 'text.secondary',
    '&.Mui-selected': {
      color: theme.palette.custom.textBlue,
      fontWeight: 500,
      backgroundColor: theme.palette.custom.sidebarActiveBg,
      borderRadius: '6px',
    },
  },
});

const ButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 16,
  marginLeft: 'auto',
});

const TabPanel: React.FC<{
  children: React.ReactNode;
  value: number;
  index: number;
}> = ({ children, value, index }) => {
  return value === index ? <>{children}</> : null;
};

const AssistantDashboardTabs: React.FC = () => {
  const { isLoading } = useAgents();
  const { selectedAssistant } = useDashboard();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = React.useState(0);

  useEffect(() => {
    const openTab = (location.state as { openTab?: number })?.openTab;
    if (typeof openTab === 'number' && openTab >= 0) {
      setSelectedTab(openTab);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const [isDeployUrlsModalOpen, setIsDeployUrlsModalOpen] =
    React.useState(false);
  const [showVersionHistory, setShowVersionHistory] = React.useState(false);
  const { alert, setAlert } = useAlert();
  const [isConfigDrawerOpen, setIsConfigDrawerOpen] = React.useState(false);
  const [selectedFunctionTab, setSelectedFunctionTab] = React.useState(0);
  const [isWebHookDrawerOpen, setIsWebHookDrawerOpen] = React.useState(false);
  const [isWebFormDrawerOpen, setIsWebFormDrawerOpen] = React.useState(false);
  const [isToolCreatorDrawerOpen, setIsToolCreatorDrawerOpen] =
    React.useState(false);
  const dispatch = useDispatch();
  const [selectedView, setSelectedView] = React.useState<'single' | 'advance'>(
    'single'
  ); // <-- add this

  const tabItems = [
    {
      label: 'Agent',
      desc: (
        <AgentInfo
          isEditable={false}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
        />
      ),
    },
    {
      label: 'Functions',
      desc: (
        <FunctionTabs
          onTabChange={tabIndex => setSelectedFunctionTab(tabIndex)}
        />
      ),
    },
    { label: 'ExtractionData', desc: <ExtractionDataTable /> },
    {
      label: 'OutboundCalls',
      desc: (
        <OutboundCalls
          agentId={selectedAssistant?._id || ''}
          agentKey={selectedAssistant?.agentKey || ''}
        />
      ),
    },
    {
      label: 'Memory',
      desc: <AgentMemory agentId={selectedAssistant?._id || ''} />,
    },
    {
      label: 'Audio Upload',
      desc: <AudioUpload />,
    },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleDeployUrlsModalClose = () => {
    setIsDeployUrlsModalOpen(!isDeployUrlsModalOpen);
  };

  // const handleVersionHistory = () => {
  //   setShowVersionHistory(true);
  // };

  const handleBackToTabs = () => {
    setShowVersionHistory(false);
  };

  const commonBoxStyles = {
    display: 'flex',
    borderRadius: '8px',
    border: '1px solid #DCE0E7',
    backgroundColor: '#FFF',
    height: '100%',
  };

  const commonContentStyles = {
    width: '100%',
  };

  if (isLoading) {
    return (
      <Box sx={commonBoxStyles}>
        <Box sx={{ ...commonContentStyles, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[...Array(4)].map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width={100}
                  height={40}
                  sx={{ borderRadius: '6px' }}
                />
              ))}
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
              <Skeleton
                variant="rectangular"
                width={100}
                height={36}
                sx={{ borderRadius: '6px' }}
              />
              <Skeleton
                variant="rectangular"
                width={140}
                height={36}
                sx={{ borderRadius: '6px' }}
              />
            </Box>
          </Box>
          <Divider />
          <Box sx={{ mt: 2, p: '0px 24px 24px' }}>{tabItems[0].desc}</Box>
        </Box>
      </Box>
    );
  }

  const handleOpenWebHookDrawer = () => {
    dispatch(resetFunctionBuilder());
    setIsWebHookDrawerOpen(true);
  };

  const handleCloseWebHookDrawer = () => {
    setIsWebHookDrawerOpen(false);
  };

  const handleOpenWebFormDrawer = () => {
    dispatch(resetWebForm());
    setIsWebFormDrawerOpen(true);
  };

  const handleCloseWebFormDrawer = () => {
    setIsWebFormDrawerOpen(false);
  };

  const handleOpenToolCreatorDrawer = () => {
    setIsToolCreatorDrawerOpen(true);
  };

  const handleCloseToolCreatorDrawer = () => {
    setIsToolCreatorDrawerOpen(false);
  };

  return (
    <Box sx={commonBoxStyles}>
      <Box sx={commonContentStyles}>
        {showVersionHistory ? (
          <VersionHistoryCards onBack={handleBackToTabs} />
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                padding: '10px 16px',
              }}
            >
              <StyledTabs value={selectedTab} onChange={handleTabChange}>
                {tabItems.map((tab, i) => (
                  <Tab key={i} label={tab.label} />
                ))}
              </StyledTabs>

              {selectedTab === 0 && (
                <ButtonContainer>
                  {/* Removed Edit, Variables, and Deploy URLs buttons - moved to AgentInfo.tsx */}
                  <Box
                    onClick={() => setIsConfigDrawerOpen(true)}
                    sx={{
                      color: theme.palette.text.secondary,
                      textTransform: 'none',
                      boxShadow: 'none',
                      cursor: 'pointer',
                      mt: 0.5,
                    }}
                  >
                    <Tooltip title="Advanced Configuration">
                      <Gear size={24} />
                    </Tooltip>
                  </Box>
                </ButtonContainer>
              )}

              {selectedTab === 1 && selectedFunctionTab === 1 && (
                <ButtonContainer>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Plus size={16} />}
                    onClick={handleOpenWebHookDrawer}
                    sx={{
                      width: 'fit-content',
                      height: '34px',
                      borderRadius: '6px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      padding: '8px 16px',
                    }}
                  >
                    Create Webhook Function
                  </Button>
                </ButtonContainer>
              )}

              {selectedTab === 1 && selectedFunctionTab === 2 && (
                <ButtonContainer>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Plus size={16} />}
                    onClick={handleOpenWebFormDrawer}
                    sx={{
                      width: 'fit-content',
                      height: '34px',
                      borderRadius: '6px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      padding: '8px 16px',
                    }}
                  >
                    Create Webform Function
                  </Button>
                </ButtonContainer>
              )}

              {selectedTab === 1 && selectedFunctionTab === 4 && (
                <ButtonContainer>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Plus size={16} />}
                    onClick={handleOpenToolCreatorDrawer}
                    sx={{
                      width: 'fit-content',
                      height: '34px',
                      borderRadius: '6px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      padding: '8px 16px',
                    }}
                  >
                    Create Tool
                  </Button>
                </ButtonContainer>
              )}
            </Box>
            <Divider />
            <Box
              sx={{
                overflow: 'none',
                flexGrow: 1,
                height: 'calc(100vh - 200px)',
                '&::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              {tabItems.map((tab, index) => (
                <TabPanel key={index} value={selectedTab} index={index}>
                  {selectedTab === 0 && index === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        height: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      <Box sx={{ flex: '7' }}>{tab.desc}</Box>
                      <Box sx={{ flex: '1.5' }}>
                        {selectedView === 'single' ? (
                          <VersionHistorySidebar />
                        ) : (
                          <FlowHistorySidebar />
                        )}
                      </Box>
                    </Box>
                  ) : (
                    tab.desc
                  )}
                </TabPanel>
              ))}
            </Box>

            <DeployUrlsModal
              open={isDeployUrlsModalOpen}
              onClose={handleDeployUrlsModalClose}
            />
            <ConfigurationDrawer
              open={isConfigDrawerOpen}
              onClose={() => setIsConfigDrawerOpen(false)}
            />
            <WebHookFunctionDrawer
              open={isWebHookDrawerOpen}
              onClose={handleCloseWebHookDrawer}
            />
            <WebFormFunctionDrawer
              open={isWebFormDrawerOpen}
              onClose={handleCloseWebFormDrawer}
              isEditing={false}
              webformData={null}
            />
            <ToolCreator
              open={isToolCreatorDrawerOpen}
              onClose={handleCloseToolCreatorDrawer}
            />
          </>
        )}
      </Box>

      <MessagePopup alert={alert} setAlert={setAlert} />
      {/* Removed VariablesModal and EditAgentDrawer - moved to AgentInfo.tsx */}
    </Box>
  );
};

export default AssistantDashboardTabs;
